import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Button, 
  Card, 
  Checkbox, 
  FormControlLabel, 
  TextField, 
  Typography,
  Container,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton
} from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ManagetNav from "../../components/ManagerNav/ManagerNav";
import './CreateManagerView.scss';

const CreateManagerView: React.FC = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [username, setUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState<string>("");
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [isActive, setIsActive] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const createManagerReq = async () => {
    setError(null);
    setLoading(true);
    
    try {
      const response = await fetch(
        `${process.env.API_HOST || ''}/api/v1/manager/create`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            Manager_Username: username,
            Manager_Password: password,
            Manager_FirstName: firstName,
            Manager_LastName: lastName,
            Manager_Email: email,
            Manager_Active: isActive
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to create manager: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      const manager = data.manager;
      
      // Navigate to the manager's page in ManagerView
      navigate(`/manager?Manager_guid=${manager.Manager_guid}`);
    } catch (error) {
      console.error("Manager creation failed", error);
      setError(error instanceof Error ? error.message : "Failed to create manager. Please check all fields and try again.");
    } finally {
      setLoading(false);
    }
  };

  const usernameValid = () => {
    const usernameRegex = /^[a-zA-Z0-9]+$/;
    return username.length >= 3 && username.length <= 50 && usernameRegex.test(username);
  };

  const passwordValid = () => {
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/;
    return password.length >= 8 && passwordRegex.test(password);
  };

  const emailValid = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const firstNameValid = () => {
    const nameRegex = /^[a-zA-Z\s]+$/;
    return firstName.length > 0 && firstName.length <= 50 && nameRegex.test(firstName);
  };

  const lastNameValid = () => {
    const nameRegex = /^[a-zA-Z\s]+$/;
    return lastName.length > 0 && lastName.length <= 50 && nameRegex.test(lastName);
  };

  const valid = () => {
    return (
      usernameValid() && 
      passwordValid() &&
      emailValid() && 
      firstNameValid() && 
      lastNameValid()
    );
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !loading && valid()) {
      createManagerReq();
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <>
      <ManagetNav />
      <Container maxWidth="sm" className="manager-create-view">
        <Card className="manager-create-card">
          <div className="manager-create-header">
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              variant="outlined"
              size="small"
            >
              Back
            </Button>
            <Typography variant="h4" className="manager-create-title">
              Create New Manager
            </Typography>
          </div>

          {error && (
            <Alert severity="error" className="manager-create-error">
              {error}
            </Alert>
          )}

          <div className="manager-create-form">
            <TextField 
              disabled={loading}
              autoFocus
              value={username}
              error={username.length > 0 && !usernameValid()}
              helperText={username.length > 0 && !usernameValid() ? "Username must be 3-50 alphanumeric characters" : ""}
              required
              onChange={(e) => setUsername(e.target.value)}
              onKeyPress={handleKeyPress}
              label="Username" 
              variant="outlined"
              fullWidth
            />
            
            <TextField 
              disabled={loading}
              value={password}
              error={password.length > 0 && !passwordValid()}
              helperText={password.length > 0 && !passwordValid() ? "Password must be 8+ chars with upper, lower, number, and special character" : ""}
              required
              onChange={(e) => setPassword(e.target.value)}
              onKeyPress={handleKeyPress}
              label="Password" 
              type={showPassword ? "text" : "password"}
              variant="outlined"
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={togglePasswordVisibility}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            <TextField 
              disabled={loading}
              value={email}
              error={email.length > 0 && !emailValid()}
              helperText={email.length > 0 && !emailValid() ? "Please enter a valid email address" : ""}
              required
              onChange={(e) => setEmail(e.target.value)}
              onKeyPress={handleKeyPress}
              label="Email" 
              type="email"
              variant="outlined"
              fullWidth
            />
            
            <TextField
              disabled={loading}
              value={firstName}
              error={firstName.length > 0 && !firstNameValid()}
              helperText={firstName.length > 0 && !firstNameValid() ? "First name must contain only letters and spaces (1-50 chars)" : ""}
              required
              onChange={(e) => setFirstName(e.target.value)}
              onKeyPress={handleKeyPress}
              label="First Name"
              variant="outlined"
              fullWidth
            />
            
            <TextField
              disabled={loading}
              value={lastName}
              error={lastName.length > 0 && !lastNameValid()}
              helperText={lastName.length > 0 && !lastNameValid() ? "Last name must contain only letters and spaces (1-50 chars)" : ""}
              required
              onChange={(e) => setLastName(e.target.value)}
              onKeyPress={handleKeyPress}
              label="Last Name"
              variant="outlined"
              fullWidth
            />
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  disabled={loading}
                />
              }
              label="Active Manager"
            />
            
            <Button 
              disabled={!valid() || loading} 
              onClick={createManagerReq} 
              variant="contained" 
              color="primary"
              fullWidth
              size="large"
              startIcon={loading ? <CircularProgress size={20} /> : <PersonAddIcon />}
            >
              {loading ? "Creating Manager..." : "Create Manager"}
            </Button>
          </div>
        </Card>
      </Container>
    </>
  );
};

export default CreateManagerView;
